import { 
    useState, 
    useEffect, 
    useMemo, 
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { withPather } from 'react-pather';
import Highlighter from "react-highlight-words";
import { 
    useTheme, 
    useMediaQuery, 
    Grid,
    Box,
    Button,
    Link, 
    Tooltip,
} from '@mui/material';
import {
    ContentCopyRounded,
    DeleteForeverRounded,
    DoneRounded,
    LinkRounded,
} from '@mui/icons-material';
import { Confirmation, WebsiteFavicon, } from '../../../general'
import { ConversionChips } from '../../../general/chips';
import { setConversion } from '../../../../slices/conversionSlice';

const defaultDateSettings = { 
    title: 'Creation date', 
    field: 'createdAt',
}

const ConversionRow = ({ 
    pather, 
    index,
    conversion, 
    hoveredIndex,
    setHoveredIndex,
    settings, 
    handleDelete, 
    isExpanded,
    highlightBorderBottom = true, 
    withDelete = false,
    dateSettings = defaultDateSettings 
}) => {

    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));
    const matchesDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const url = new URL(conversion.source);
    const slug = pather.reverse(pather.front.main, { code: conversion.code });
    const composedUrl = `${window.location.origin}${slug}`;

    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isCurrentHovered, setIsCurrentHovered] = useState(false);
    const { query, freshness } = settings;

    useEffect(() => {
        if(!copied) return;
        const intervalId = setInterval(() => {
            setCopied(false);
        }, 1000)
        return () => clearInterval(intervalId);
    }, [copied]);

    const isBigCodeBelowMd = useMemo(() => () => matchesDownMd && conversion.code.length > 7, [matchesDownMd, conversion]);

    const copy = e => {
        if(copied) setCopied(false);
        navigator.clipboard.writeText(composedUrl);
        setCopied(true);
        e.stopPropagation();
    }

    const view = () => {
        dispatch(setConversion(conversion));
        navigate(pather.reverse(pather.front.view, { code: conversion.code }));
    }

    const {
        title, 
        field
    } = dateSettings;

    const date = {
        title,
        value: conversion[field]
    }

    const decodedURL = url.href;

    return (
        <Box 
            sx={{
                borderBottom: theme.palette.tinyBorder,
                pb: 2,
                pt: isExpanded ? 3 : 2,
                cursor: 'pointer', 
                display: 'flex',
                ...(hoveredIndex === index + 1 ? { borderBottom: "none" } : {}),
                '&:hover': {
                    borderRadius: '12px',
                    borderBottom: highlightBorderBottom && freshness === 'all' && !conversion.markers.isActive && '2px solid #DA2D2D', 
                    border: theme.palette.tinyBorder,
                    px: 1,
                    backgroundColor: theme.palette.action.hover,
                    boxShadow: theme.palette.squareBoxShadow,
                },
                transition: 'all 0.3s ease',
            }}
            onMouseEnter={() => {
                setHoveredIndex(index);
                setIsCurrentHovered(true);
            }}
            onMouseLeave={() => {
                setHoveredIndex(null);
                setIsCurrentHovered(false);
            }}
            onClick={view}
        >
            <Grid container>
                <Grid item xs={withDelete ? 10 : 12}>
                    <Box sx={{
                        width: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 2
                    }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', 
                            flexDirection: withDelete || isBigCodeBelowMd() ? 'column' : 'row', gap: (withDelete || isBigCodeBelowMd()) && 2 
                        }}>
                            <Box sx={{
                                flex: 1,
                                overflowX: "auto",
                                mr: 2,
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1, 
                                fontSize: '26px',
                                alignSelf: (withDelete || isBigCodeBelowMd()) && 'flex-start' 
                            }}>
                                { !isExpanded && <LinkRounded/> }
                                <Box sx={{
                                    overflowX: 'auto', 
                                    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light', 
                                    whiteSpace: 'nowrap',
                                    "&::-webkit-scrollbar": {
                                        height: "8px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: theme.palette.mode === "light" ? "#c1c1c1" : "#6b6b6b",
                                        borderRadius: "4px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: theme.palette.mode === "light" ? (isCurrentHovered ? "#E6E6E6" : "#F0F0F0") : theme.palette.background.paper,
                                        borderRadius: "4px",
                                    },
                                }}>
                                    <Link 
                                        href={composedUrl}
                                        target="_blank" rel="noreferrer" 
                                        sx={{
                                            ...theme.link, 
                                            cursor: 'pointer',
                                        }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <Highlighter
                                            highlightStyle={{
                                                color: theme.palette.textMatchColor, 
                                                backgroundColor: 'transparent',
                                            }}
                                            searchWords={[query]}
                                            autoEscape={true}
                                            textToHighlight={ matches ? conversion.code : composedUrl }
                                        />
                                    </Link>
                                </Box>
                                <Box>
                                    {
                                        copied ?
                                        <DoneRounded fontSize="small" sx={{color: 'green'}} onClick={e => e.stopPropagation()}/>  :
                                        <ContentCopyRounded fontSize='small' onClick={copy}/>
                                    }
                                </Box>
                            </Box>
                            <Box sx={{marginLeft: (!withDelete && !isBigCodeBelowMd()) && 'auto', alignSelf: (withDelete || isBigCodeBelowMd()) && 'flex-start'}}>
                                <Tooltip title={date.title} placement={withDelete ? 'right' : (isExpanded ? 'bottom' : 'left')} arrow>
                                    <span>
                                        {
                                            matchesDownMd ?
                                            new Date(date.value).toLocaleDateString() : 
                                            date.value
                                        }
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                        {
                            isExpanded &&
                            <>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                }}>
                                    <WebsiteFavicon url={decodedURL} />
                                    <Box
                                        sx={{
                                            colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
                                            maxHeight: "88px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            "&::-webkit-scrollbar": {
                                                display: isCurrentHovered ? 'block' : 'none',
                                                width: "8px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: theme.palette.mode === "light" ? "#c1c1c1" : "#6b6b6b",
                                                borderRadius: "4px",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                                backgroundColor: theme.palette.mode === "light" ? (isCurrentHovered ? "#E6E6E6" : "#F0F0F0") : theme.palette.background.default,
                                                borderRadius: "4px",
                                            },
                                        }}
                                    >
                                        <Highlighter
                                            highlightStyle={{color: '#F98404', backgroundColor: 'transparent'}}
                                            searchWords={[query]}
                                            autoEscape={true}
                                            textToHighlight={decodedURL}
                                        />
                                    </Box>
                                </Box>
                                <ConversionChips conversion={conversion} size='small' />
                            </>
                        }
                    </Box>
                </Grid>
                {
                    withDelete &&
                    <Grid item xs={2}>
                        <Box sx={{
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-end', 
                            height: '99%',
                        }}>
                            <Button 
                                variant="contained" 
                                sx={{
                                    height: '4.25rem', 
                                    width: '4.25rem',  
                                    backgroundColor: '#BF3131',
                                    borderRadius: '10px',
                                    '&:hover': {
                                        backgroundColor: '#7D0A0A',
                                    }
                                }}
                                onClick={e => {
                                    setOpenConfirmDelete(true);
                                    e.stopPropagation();
                                }}
                            >
                                <DeleteForeverRounded sx={{fontSize: '1.7rem'}} />
                            </Button>
                        </Box>
                    </Grid>
                }
            </Grid>
            <Confirmation 
                open={openConfirmDelete}
                setOpen={setOpenConfirmDelete}
                title="Conversion deletion"
                description={
                    <>
                        Are you sure you want to delete conversion with a code <b>{conversion.code}</b> forever? 
                        This action <b>cannot</b> be reverted.
                    </>
                }
                success={e => {
                    handleDelete(conversion.code);
                    e.stopPropagation();
                }}
            />
        </Box>
    );
}

export default withPather()(ConversionRow);